import { useEffect, useRef, useState } from 'react';
import ButtonLoading from '@mui/lab/LoadingButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { toast } from 'react-toastify';
import { getErrorMessage } from 'utils/Error';
import {
  isCubeRefreshRunTerminal,
  useGetCubeRefreshRun,
  useRefreshCubeData,
} from 'services/v1/Tenant/AROCube/CubeDataEntryService';
import { ModalConfirmationComponent } from 'components/ModalComponent/ModalConfirmationComponent';

type CubeRefreshScope = 'MEMBER' | 'CUBE';

interface PendingCubeRefresh {
  scope: CubeRefreshScope;
  memberId?: string;
  memberLabel?: string;
}

interface ActiveCubeRefresh extends PendingCubeRefresh {
  cubeId: string;
  runId: string;
}

interface CubeRefreshButtonProps {
  cubeId: string;
  memberId?: string;
  memberLabel?: string;
  isBaseMember: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onCompleted?: () => void;
  onActiveChange?: (active: boolean) => void;
}

function getRefreshConfirmationMessage(refresh: PendingCubeRefresh | null) {
  if (refresh?.scope === 'MEMBER') {
    return `Refresh all cube data for ${refresh.memberLabel || 'the selected member'}? The latest values will be loaded when the refresh completes.`;
  }
  return 'Refresh the entire cube? This runs in the background and the latest values will be loaded when it completes.';
}

export function CubeRefreshButton(props: Readonly<CubeRefreshButtonProps>) {
  const { cubeId, memberId, memberLabel, isBaseMember, disabled, disabledReason, onCompleted, onActiveChange } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pendingRefresh, setPendingRefresh] = useState<PendingCubeRefresh | null>(null);
  const [activeRefresh, setActiveRefresh] = useState<ActiveCubeRefresh | null>(null);
  const handledRunIdsRef = useRef<Set<string>>(new Set());

  const { mutate: refreshCube, isLoading: isStartingRefresh } = useRefreshCubeData();
  const {
    data: refreshRunResponse,
    isError: isRefreshStatusError,
    error: refreshStatusError,
  } = useGetCubeRefreshRun(activeRefresh?.cubeId, activeRefresh?.runId);
  const refreshRun = refreshRunResponse?.data;
  const isRefreshing = isStartingRefresh || !!activeRefresh;

  // The cube activity status only turns RECALCULATING once the worker picks the run up, so report the
  // queued run as well; the cleanup also covers finishing, failing, switching cube and unmounting.
  useEffect(() => {
    if (!activeRefresh) return undefined;
    onActiveChange?.(true);
    return () => onActiveChange?.(false);
  }, [activeRefresh, onActiveChange]);

  useEffect(() => {
    if (!activeRefresh || !refreshRun || !isCubeRefreshRunTerminal(refreshRun)) return;
    if (handledRunIdsRef.current.has(activeRefresh.runId)) return;

    handledRunIdsRef.current.add(activeRefresh.runId);
    if (refreshRun.status === 'FAILED' || refreshRun.errorMessage) {
      toast.error(refreshRun.errorMessage || 'Cube refresh failed');
    } else {
      const rowCount = refreshRun.rowsWrittenCount ?? 0;
      const target = activeRefresh.scope === 'MEMBER' ? activeRefresh.memberLabel || 'Selected member' : 'Cube';
      toast.success(`${target} refreshed. ${rowCount} row${rowCount === 1 ? '' : 's'} written.`);
      onCompleted?.();
    }
    setActiveRefresh(null);
  }, [activeRefresh, onCompleted, refreshRun]);

  useEffect(() => {
    if (!activeRefresh || !isRefreshStatusError) return;
    if (handledRunIdsRef.current.has(activeRefresh.runId)) return;

    handledRunIdsRef.current.add(activeRefresh.runId);
    toast.error(getErrorMessage(refreshStatusError));
    setActiveRefresh(null);
  }, [activeRefresh, isRefreshStatusError, refreshStatusError]);

  useEffect(() => {
    setAnchorEl(null);
    setPendingRefresh(null);
    setActiveRefresh(null);
    handledRunIdsRef.current.clear();
  }, [cubeId]);

  useEffect(() => {
    if (!disabled) return;
    setAnchorEl(null);
    setPendingRefresh(null);
  }, [disabled]);

  const handleSelectRefresh = (refresh: PendingCubeRefresh) => {
    if (disabled) return;
    setAnchorEl(null);
    setPendingRefresh(refresh);
  };

  const handleStartRefresh = () => {
    if (disabled || !pendingRefresh || isRefreshing) return;
    const refresh = pendingRefresh;
    setPendingRefresh(null);

    refreshCube(
      {
        cubeId,
        memberId: refresh.scope === 'MEMBER' ? refresh.memberId : undefined,
      },
      {
        onSuccess: (response) => {
          if (!response.data?.runId) {
            toast.error('The cube refresh did not return a run ID');
            return;
          }
          setActiveRefresh({ ...refresh, cubeId, runId: response.data.runId });
          toast.info(refresh.scope === 'MEMBER' ? 'Member refresh queued.' : 'Cube refresh queued.');
        },
        onError: (error) => {
          toast.error(getErrorMessage(error));
        },
      },
    );
  };

  const buttonLabel = refreshRun?.status === 'IN_QUEUE' ? 'Refresh queued' : isRefreshing ? 'Refreshing data' : 'Refresh Data';

  return (
    <>
      <Tooltip title={disabled ? disabledReason || '' : ''} arrow>
        <span>
          <ButtonLoading
            variant='main-table-panel-border'
            disableElevation
            disabled={disabled || isRefreshing}
            loading={isRefreshing}
            loadingPosition='start'
            startIcon={<LoopOutlinedIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(event) => { if (!disabled) setAnchorEl(event.currentTarget); }}
            aria-haspopup='menu'
            aria-expanded={Boolean(anchorEl)}
          >
            {buttonLabel}
          </ButtonLoading>
        </span>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem
          disabled={!memberId || !isBaseMember}
          onClick={() =>
            handleSelectRefresh({
              scope: 'MEMBER',
              memberId,
              memberLabel,
            })
          }
        >
          <ListItemIcon>
            <PersonOutlineOutlinedIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText
            primary='Refresh selected member'
            secondary={isBaseMember ? memberLabel : 'Select a base-level member'}
          />
        </MenuItem>
        <MenuItem onClick={() => handleSelectRefresh({ scope: 'CUBE' })}>
          <ListItemIcon>
            <ViewInArOutlinedIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText primary='Refresh entire cube' />
        </MenuItem>
      </Menu>

      <ModalConfirmationComponent
        visible={!!pendingRefresh && !disabled}
        title={pendingRefresh?.scope === 'MEMBER' ? 'Refresh Selected Member' : 'Refresh Entire Cube'}
        message={getRefreshConfirmationMessage(pendingRefresh)}
        messageType='info'
        labelButtonApprove='Refresh'
        onApprove={handleStartRefresh}
        onCancel={() => setPendingRefresh(null)}
        onClose={() => setPendingRefresh(null)}
      />
    </>
  );
}
